import { createSlice } from '@reduxjs/toolkit';
//
import { dispatch } from '../store';
// slice
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';

// -----------------------------------------------

const initialState = {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
};

const slice = createSlice({
    name: 'taskNotification',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        setNotifications(state, action) {
            state.isLoading = false;
            // Defensive normalisation. The task-notifications endpoint has
            // historically been served both as a bare array and as a paginated
            // `{ results: [], count }` envelope (and some deploys still return
            // a 404 object). Without this guard `state.notifications` could
            // land as `undefined` or a plain object, which later caused
            // "notifications is not iterable" the moment any spread/iterate
            // hit it (e.g. addNotification or NavBar selectors).
            const { payload } = action;
            if (Array.isArray(payload)) {
                state.notifications = payload;
            } else if (Array.isArray(payload?.results)) {
                state.notifications = payload.results;
            } else {
                state.notifications = [];
            }
        },
        setUnreadCount(state, action) {
            state.isLoading = false;
            state.unreadCount = action.payload;
        },
        addNotification(state, action) {
            // Guard against a non-array slice (see setNotifications note).
            // Spreading a non-iterable here used to throw and bubble up to
            // the route boundary as "e.notifications is not iterable".
            const current = Array.isArray(state.notifications) ? state.notifications : [];
            state.notifications = [action.payload, ...current];
        },
        incrementUnreadCount(state) {
            state.unreadCount += 1;
        },
    }
});

export default slice.reducer;

export const { reset, startLoading, setNotifications, setUnreadCount, addNotification, incrementUnreadCount } = slice.actions;

export function getUnreadNotifications(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/task-notifications/`);
            if (response.status === 200) {
                dispatch(slice.actions.setNotifications(response.data));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function markNotificationsRead(practiceId, notificationIds) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-notifications/mark-read/`, 'post', { notification_ids: notificationIds });
            if (response.status !== 200) {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getUnreadCount(practiceId) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/task-notifications/unread-count/`);
            if (response.status === 200) {
                const count = typeof response.data === 'number'
                    ? response.data
                    : (response.data?.count ?? response.data?.unread_count ?? 0);
                dispatch(slice.actions.setUnreadCount(count));
            }
            // Silently ignore non-200 (e.g. 404 when backend not deployed)
        } catch (error) {
            // Silently ignore errors -- task notification endpoint may not exist yet
        }
    };
}
